import { Component, ElementRef, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NavbarComponent } from "../navbar/navbar.component";
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { NgxChartsModule } from '@swimlane/ngx-charts';

@Component({
  selector: 'app-landing-page',
  imports: [MatIconModule, NavbarComponent, CommonModule, FormsModule, NgxChartsModule],
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  standalone: true
})
export class LandingPageComponent {
  @ViewChild('chatContainer') chatContainer!: ElementRef;
  selectedTab: 'inbound' | 'outbound' = 'inbound';
  uploadedFiles: File[] = [];
  entityName: string = '';
  chatMessages: {
    type: 'user' | 'bot' | 'table' | 'chart';
    text?: string;
    columns?: string[];
    tableData?: any[];
    chartType?: string;
    chartData?: any[];
  }[] = [];
  hasUserSentMessage: boolean = false;
  showChartsPanel: boolean = false;
  activeChart: {
    chartType: string;
    chartData: any[];
  } | null = null;
  sessionId: string = 'riya2'; // Fixed sessionId from the image
  isLoading: boolean = false; // New property for loading state

  constructor(
    private http: HttpClient,
    private cdRef: ChangeDetectorRef,
    private router: Router
  ) {}

  onTabChange(tab: 'inbound' | 'outbound') {
    this.selectedTab = tab;
    this.resetChat();
  }

  resetChat(): void {
    this.uploadedFiles = [];
    this.entityName = '';
    this.chatMessages = [];
    this.hasUserSentMessage = false;
    this.showChartsPanel = false;
    this.activeChart = null;
  }

  navigateTohome() {
    if (this.selectedTab === 'inbound') {
      this.router.navigate(['/home']);
    }
  }

  onFileSelected(event: Event): void {
    this.hasUserSentMessage = true;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      // const csvFiles = files.filter(file => file.name.endsWith('.csv'));
      const jsonFiles = files.filter(file => file.name.endsWith('.json'));

      if (this.selectedTab === 'outbound') {
        if (jsonFiles.length !== 1 || files.length !== 1) {
          this.chatMessages.push({
            type: 'bot',
            text: 'Please upload exactly one JSON file.'
          });
          return;
        }
        // this.chatMessages.push({
        //   type: 'bot',
        //   text: 'Please provide the entity name (table name) for the uploaded file.'
        // });
      }

      this.uploadedFiles = files;
    }
    this.scrollToBottom();
  }

  showChart(chartMsg: { type: string; chartType?: string; chartData?: any[] }): void {
    if (chartMsg.chartType && chartMsg.chartData) {
      this.activeChart = {
        chartType: chartMsg.chartType,
        chartData: chartMsg.chartData
      };
      this.showChartsPanel = true;
      this.cdRef.detectChanges();
    }
  }

  onSendMessage(): void {
    this.hasUserSentMessage = true;
  
    // Close chart panel and clear active chart when sending a new message
    this.showChartsPanel = false;
    this.activeChart = null;
  
    const userInput = this.entityName.trim(); // Capture the input before clearing
    if (userInput) {
      if (this.uploadedFiles.length > 0) {
        const fileNames = this.uploadedFiles.map(file => file.name).join(', ');
        this.chatMessages.push({
          type: 'user',
          text: `You uploaded: ${fileNames}`
        });
        this.scrollToBottom(); // Scroll after adding upload message
      }
  
      this.chatMessages.push({ type: 'user', text: userInput });
      this.scrollToBottom(); // Scroll after adding user input
  
      // Set loading state and clear input immediately
      this.isLoading = true;
      this.entityName = ''; // Clear the user input right away
      this.cdRef.detectChanges(); // Force UI to reflect the clear
  
      // Prepare FormData for POST request
      const formData = new FormData();
      formData.append('sessionId', this.sessionId);
      formData.append('user_input', userInput); // Use the captured input for API
      if (this.uploadedFiles.length > 0) {
        formData.append('config_file', this.uploadedFiles[0]);
      }
  
      // Make POST request to the greeting-table endpoint
      this.http.post('http://localhost:5000/v1/greeting-table', formData).subscribe({
        next: (response: any) => {
          console.log('API Response:', response);
          if (response.status === 'success') {
            if (response.message) {
              this.chatMessages.push({
                type: 'bot',
                text: response.message
              });
              this.scrollToBottom(); // Scroll after adding bot message
              console.log('Message added:', response.message);
            }
  
            let tableData = response.table_data || (response.data && response.data.table_data);
            if (tableData && tableData.length > 0) {
              this.chatMessages.push({
                type: 'table',
                columns: Object.keys(tableData[0] || {}),
                tableData: tableData
              });
              this.scrollToBottom(); // Scroll after adding table
              console.log('Table data added:', tableData);
            } else {
              console.log('No table data found in response');
            }
  
            let chartData = response.data && response.data.chart_data;
            if (chartData && chartData.length > 0) {
              const chartType = response.message.toLowerCase().includes('bar chart') ? 'bar' : 'line'; // Default to 'bar' or infer
              this.chatMessages.push({
                type: 'chart',
                chartType: chartType,
                chartData: chartData
              });
              this.scrollToBottom(); // Scroll after adding chart
              console.log('Chart data added:', { chartType, chartData });
            } else {
              console.log('No chart data found in response');
            }
  
            if (response.prompt) {
              this.chatMessages.push({
                type: 'bot',
                text: response.prompt
              });
              this.scrollToBottom(); // Scroll after adding prompt
              console.log('Prompt added:', response.prompt);
            } else {
              console.log('No prompt in response');
            }
          } else {
            this.chatMessages.push({
              type: 'bot',
              text: response.message || 'Failed to retrieve table data.'
            });
            this.scrollToBottom(); // Scroll after adding error message
          }
  
          // Reset loading state
          this.isLoading = false;
          this.uploadedFiles = [];
          this.cdRef.detectChanges();
          setTimeout(() => this.scrollToBottom(), 0); // Final scroll to ensure visibility
          console.log('Current chatMessages:', this.chatMessages);
        },
        error: (err) => {
          console.error('API call failed', err);
          const errorMessage = err.error?.message || err.message || 'An unexpected error occurred. Please try again.';
          this.chatMessages.push({
            type: 'bot',
            text: errorMessage
          });
          this.scrollToBottom(); // Scroll after adding error message
          this.isLoading = false; // Reset loading state on error
          this.uploadedFiles = [];
          this.cdRef.detectChanges();
          this.scrollToBottom();
        }
      });
    }
  }

  closeChartsPanel(): void {
    this.showChartsPanel = false;
    this.activeChart = null;
    this.cdRef.detectChanges();
  }

  scrollToBottom(): void {
    try {
      const el = this.chatContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    } catch (err) {
      console.error('Scroll failed', err);
    }
  }
}